import { IComponentOptions, IFormController, IPromise } from "angular";
import { IMetric } from "@gtmhub/okrs/metrics";
import { IGoal, IOkrTemplate } from "@gtmhub/goals/models";
import { IPlanningSession } from "@gtmhub/sessions";
import { ITask } from "@gtmhub/tasks";
import { IEmployee } from "@gtmhub/employees";
import { ITeam } from "@gtmhub/teams";
import { IList } from "@gtmhub/lists/models/models";
import { IInsight } from "@gtmhub/insights";
import { IInsightboard } from "@gtmhub/insightboards";
import { IKpi } from "@gtmhub/kpis";
import { AutomationTriggerType, IAutomation } from "@gtmhub/automation/models/automation";
import { IRole } from "@gtmhub/roles";

export type SingleCollectionSearchItemFields = IPlanningSession &
  IGoal &
  IMetric &
  ITask &
  IEmployee &
  ITeam &
  IInsightboard &
  IInsight &
  IAutomation<AutomationTriggerType> &
  IKpi &
  IList &
  IRole &
  IOkrTemplate;

export type SingleCollectionSearchItem = {
  id: string;
  name: string;
  showIcon?: boolean;
  icon?: string;
  showAvatar?: boolean;
  showTeamAvatar?: boolean;
  fields: SingleCollectionSearchItemFields;
};

type SearchMode = "open" | "closed";

export class SingleCollectionSearchCtrl {
  mode: SearchMode;
  searchTerm: string;
  indicators: {
    gettingSearchData: boolean;
  };
  shouldResizeMenu: boolean;
  resultsCollection: SingleCollectionSearchItem[];

  // ---- Bindings ---- //
  initSearchTerm: string;
  sectionTitle: string;
  searchPlaceholder: string;
  formReference: IFormController;
  externalLinkLabel: string;
  isRequired: boolean;
  onSearchInputChangeCb: ({ searchTerm }: { searchTerm: string }) => IPromise<SingleCollectionSearchItem[]>;
  onItemSelectionCb: ({ item }: { item: SingleCollectionSearchItemFields }) => unknown;
  externalLinkActionCb: () => unknown;
  onMenuResize?: ({ itemCount }: { itemCount: number }) => unknown;

  constructor() {
    this.indicators = {
      gettingSearchData: false,
    };
    this.resultsCollection = [];
  }

  $onInit(): void {
    this.mode = this.initSearchTerm ? "open" : "closed";
    this.shouldResizeMenu = !!this.onMenuResize;

    if (this.initSearchTerm) {
      this.searchTerm = this.initSearchTerm;
      this.onSearchInputChange(this.initSearchTerm);
    }
  }

  onSearchInputChange(searchTerm: string): void {
    this.indicators.gettingSearchData = !!searchTerm;

    this.onSearchInputChangeCb({ searchTerm }).then(
      (results) => {
        this.resultsCollection = results;

        if (searchTerm) {
          this.indicators.gettingSearchData = false;

          if (this.resultsCollection.length) {
            this.shouldResizeMenu && this.onMenuResize({ itemCount: this.resultsCollection.length });
            this.changeMode("open");
            return;
          }
        }

        this.shouldResizeMenu && this.onMenuResize({ itemCount: 0 });
        this.changeMode("closed");
      },
      () => {
        this.resultsCollection = [];
        this.shouldResizeMenu && this.onMenuResize({ itemCount: 0 });
        this.indicators.gettingSearchData = false;
      }
    );
  }

  select(selectedItem: SingleCollectionSearchItem): void {
    this.searchTerm = selectedItem.name;
    this.onSearchInputChange(selectedItem.name);

    this.onItemSelectionCb({ item: selectedItem.fields });
  }

  closeSearchMenu(event: JQueryEventObject): void {
    // Do not close the element if the user clicked on part of the search body
    const searchClasses = ["search-actions", "search-field"];

    if (!searchClasses.some((c) => event.target.classList.contains(c))) {
      this.shouldResizeMenu && this.onMenuResize({ itemCount: 0 });
      this.changeMode("closed");
    }
  }

  onItemHover($event: JQueryEventObject, isHovered: boolean): void {
    const selectedElement = document.querySelector(".selected-ss-item");
    if (selectedElement && selectedElement.classList.contains("single-search-item")) {
      selectedElement.classList.remove("selected-ss-item");
    }

    if (isHovered) {
      $event.target.classList.add("selected-ss-item");
    } else {
      $event.target.classList.remove("selected-ss-item");
    }
  }

  private changeMode(mode: "open" | "closed"): void {
    this.mode = mode;
  }
}

export const SingleCollectionSearchComponent: IComponentOptions = {
  templateUrl: require("./single-collection-search.jade"),
  controller: SingleCollectionSearchCtrl,
  bindings: {
    initSearchTerm: "<",
    sectionTitle: "<",
    searchPlaceholder: "<",
    formReference: "<",
    externalLinkLabel: "<",
    isRequired: "<",
    onSearchInputChangeCb: "&",
    onItemSelectionCb: "&",
    externalLinkActionCb: "&",
    onMenuResize: "&",
  },
};
